import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { type ColumnDef, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import Button from "~/components/ui/button";
import { TOAST_MESSAGES } from "~/constants/messages";
import InviteProjectModal from "~/features/projectTop/inviteProjectModal";
import { ProjectApi } from "~/functions/api/project";
import { useProjectPermission } from "~/hooks/useProjectPermission";
import ContextMenu from "~/images/context_menu.svg?react";
import { addMembers, removeMember, updateMember } from "~/slices/projectSlice";
import type { RootState } from "~/store";
import styles from "./index.module.scss";

const ROLE_MAP: { [key: string]: string } = {
  owner: "オーナー",
  contributor: "編集者",
  member: "閲覧者",
};

const ROLE_MAP_JAPANESE: { [key: string]: string } = {
  オーナー: "owner",
  編集者: "contributor",
  閲覧者: "member",
};

const mapRoleToJapanese = (role: string): string => {
  return ROLE_MAP[role.toLowerCase()] || role;
};

// データを変換するヘルパー関数
const mapDataWithLocalizedRoles = <TData extends object>(dataToMap: TData[]): TData[] => {
  return dataToMap.map((item) => {
    const role = (item as any).role;
    if (typeof role === "string") {
      return { ...item, role: mapRoleToJapanese(role) };
    }
    return item;
  });
};

const ProjectMemberContent = () => {
  const { currentUser, currentProject } = useSelector((state: RootState) => state.auth);
  const { members } = useSelector((state: RootState) => state.project);
  const { canEditProject } = useProjectPermission();

  // Propsで渡されるdataを内部的なstateとして管理
  const parsedMembers = useMemo(() => mapDataWithLocalizedRoles(members), [members]);

  const [isInviteUserModalOpen, setInviteUserModalOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState<number | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [editingRoleForUser, setEditingRoleForUser] = useState<string | null>(null);

  const columns: ColumnDef<object>[] = [
    {
      header: "表示名",
      accessorKey: "displayName",
    },
    {
      header: "権限",
      accessorKey: "role",
    },
  ];

  const dispatch = useDispatch();

  const list = useReactTable({
    data: parsedMembers,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleConfirmRoleChange = async (userId: string, newRole: string) => {
    const projectApi = new ProjectApi();
    // currentUser や currentProject が null/undefined の場合のチェックを追加
    if (!currentUser?.organization_id || !currentProject?.id || !currentUser?.id) {
      toast.error(TOAST_MESSAGES.PROJECT_MEMBER.MISSING_INFO);
      setEditingRoleForUser(null);
      setDropdownOpen(null);
      return;
    }

    const response = await projectApi.updateRole(
      currentUser.organization_id,
      currentProject.id,
      userId,
      { role: ROLE_MAP_JAPANESE[newRole]! },
    );
    if (response === true) {
      toast.success(TOAST_MESSAGES.PROJECT_MEMBER.CHANGE_ROLE_SUCCESS);
      dispatch(updateMember({ userId, newRole }));
    } else {
      toast.error(TOAST_MESSAGES.PROJECT_MEMBER.CHANGE_ROLE_FAILED);
    }
    setEditingRoleForUser(null); // 権限編集モードを解除
    setDropdownOpen(null); // ドロップダウン全体を閉じる
  };

  const handleRemoveUser = async (userId: string) => {
    const projectApi = new ProjectApi();
    // currentUser や currentProject が null/undefined の場合のチェックを追加
    if (!currentUser?.organization_id || !currentProject?.id || !currentUser?.id) {
      toast.error(TOAST_MESSAGES.PROJECT_MEMBER.MISSING_INFO);
      setDropdownOpen(null);
      return;
    }
    const response = await projectApi.removeUser(
      currentUser.organization_id,
      currentProject.id,
      userId,
    );
    if (response !== null) {
      toast.success(TOAST_MESSAGES.PROJECT_MEMBER.DELETE_USER_SUCCESS);
      dispatch(removeMember(userId));
    } else {
      toast.error(TOAST_MESSAGES.PROJECT_MEMBER.DELETE_USER_FAILED);
    }
    setDropdownOpen(null);
  };

  // 管理者（オーナー）権限があるかどうかを確認
  const hasPermission =
    currentUser &&
    list.getRowModel().rows.some((row) => {
      const userName = currentUser.display_name;
      const role = row
        .getVisibleCells()
        .find((cell) => cell.column.id === "role")
        ?.getValue() as string;
      const name = row
        .getVisibleCells()
        .find((cell) => cell.column.id === "displayName")
        ?.getValue() as string;

      return role === ROLE_MAP["owner"] && name === userName;
    });

  return (
    <div className={styles["ProjectTopListContent"]}>
      <div className={styles["title"]}>
        <h3>メンバー</h3>
        <Button
          label="メンバー追加"
          styleType="blue"
          onClick={() => setInviteUserModalOpen(true)}
          disabled={!canEditProject()}
        />
      </div>
      <ul className={styles["memberList"]}>
        {list
          .getRowModel()
          .rows.sort((a, b) => {
            // Define role priorities
            const rolePriority: Record<string, number> = {
              オーナー: 1, // Owner
              編集者: 2, // Editor
              閲覧者: 3, // Viewer
            };

            const roleA =
              a
                .getVisibleCells()
                .find((cell) => cell.column.id === "role")
                ?.getValue() || "";
            const roleB =
              b
                .getVisibleCells()
                .find((cell) => cell.column.id === "role")
                ?.getValue() || "";

            // Use priorities for sorting
            return (rolePriority[roleA as string] || 99) - (rolePriority[roleB as string] || 99);
          })
          .map((row, index) => {
            const rowData = row.original as any;
            const userId = rowData.id;

            // 現在の行のユーザーの役割を取得
            const userCurrentRole = row
              .getVisibleCells()
              .find((cell) => cell.column.id === "role")
              ?.getValue() as string;

            const availableRoles = ["オーナー", "編集者", "閲覧者"];

            return (
              <li className={styles["memberItem"]} key={row.id}>
                <div className={styles["memberAvatar"]}>
                  <AccountCircleIcon style={{ fontSize: 40, color: "#1D9FD6" }} />
                </div>
                <div className={styles["memberDetails"]}>
                  {row.getVisibleCells().map((cell) => (
                    <span
                      key={cell.id}
                      className={
                        cell.column.id === "role"
                          ? `${styles["memberRole"]} ${
                              cell.getValue() === "オーナー"
                                ? styles["ownerRole"]
                                : cell.getValue() === "編集者"
                                  ? styles["editorRole"]
                                  : styles["viewerRole"]
                            }`
                          : styles["memberInfo"]
                      }
                    >
                      {cell.column.id === "role" ? (
                        <span>{cell.getValue() as string | number}</span>
                      ) : (
                        (cell.getValue() as string)
                      )}
                    </span>
                  ))}
                </div>
                <div
                  className={styles["moreOptions"]}
                  ref={index === dropdownOpen ? dropdownRef : null}
                >
                  {hasPermission && userId !== currentUser?.id && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setDropdownOpen(dropdownOpen === index ? null : index);
                      }}
                    >
                      <ContextMenu />
                    </button>
                  )}

                  {dropdownOpen === index && hasPermission && (
                    <div className={styles["dropdownMenu"]}>
                      {editingRoleForUser === userId ? (
                        // 権限選択UI
                        <ul>
                          {availableRoles.map((role) => (
                            <li
                              key={role}
                              className={`${userCurrentRole === role ? styles["currentRole"] : ""}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleConfirmRoleChange(userId, role);
                              }}
                            >
                              {role} {userCurrentRole === role && " (現在)"}
                            </li>
                          ))}
                          <li
                            className={styles["cancelOption"]}
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingRoleForUser(null); // キャンセル
                            }}
                          >
                            キャンセル
                          </li>
                        </ul>
                      ) : (
                        // 通常メニュー
                        <ul>
                          <li
                            onClick={(e) => {
                              e.stopPropagation();
                              // handleRoleChange(userId); // 古い処理はコメントアウトまたは削除
                              setEditingRoleForUser(userId); // 権限変更モードに移行
                            }}
                          >
                            権限変更
                          </li>
                          <li
                            className={styles["danger"]}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveUser(userId);
                            }}
                          >
                            削除
                          </li>
                          <li
                            onClick={(e) => {
                              e.stopPropagation();
                              setDropdownOpen(null);
                            }}
                          >
                            キャンセル
                          </li>
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
      </ul>
      <InviteProjectModal
        isAdmin={false}
        isOpen={isInviteUserModalOpen}
        onClose={() => setInviteUserModalOpen(false)}
        onMembersAdded={(addedMembers) => dispatch(addMembers(addedMembers))}
      />
    </div>
  );
};

export default ProjectMemberContent;
